'use client';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  type DialogProps as MDialogProps,
} from '@mui/material';
import { usePathname } from 'next/navigation';
import {
  createContext,
  Dispatch,
  ReactElement,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

// Renderer signature for custom dialogs
export type DialogRenderFn<T = unknown> = (helpers: {
  close: (result?: T) => void;
}) => ReactNode;

export type DialogOptions = Pick<
  MDialogProps,
  'fullScreen' | 'fullWidth' | 'maxWidth' | 'sx'
> & {
  ariaLabel?: string;
};

export type BasicModalContentProps<TData> = {
  data: TData;
  setData: Dispatch<SetStateAction<TData>>;
  close: (v?: TData) => void;
};

export type BasicModalPayload<TData = unknown> = {
  title?: ReactNode;
  content?: ReactNode | ((ctx: BasicModalContentProps<TData>) => ReactNode);
  cancelText?: ReactNode;
  confirmText?: ReactNode;
  initialData?: TData;
  canConfirm?: (data: TData) => boolean;
};

export type ConfirmPayload = {
  title?: ReactNode;
  content?: ReactNode;
  confirmText?: ReactNode;
  cancelText?: ReactNode;
};

type StackItem = {
  id: number;
  render: DialogRenderFn | null;
  resolver: (value: unknown) => void;
  rejecter: (reason?: unknown) => void;
  options?: DialogOptions;
  confirmPayload?: ConfirmPayload;
  basicPayload?: BasicModalPayload;
};

// Context shape
export type OpenFn = {
  <T = unknown>(
    render: DialogRenderFn<T>,
    options?: DialogOptions,
  ): Promise<T | undefined>;
  <TData = unknown>(
    payload?: BasicModalPayload<TData>,
    options?: DialogOptions,
  ): Promise<TData | undefined>;
};

export type DialogContextValue = {
  /**
   * Ouvre une modale avec le contenu spécifié.
   */
  open: OpenFn;
  /**
   * Ouvre une modale de confirmation avec le contenu et les options spécifiées.
   * Retourne une promesse qui se résout à `true` si l'utilisateur confirme, ou `false` s'il annule.
   * @param {ConfirmPayload} payload contenu de la modale de confirmation { content, confirmText, cancelText }
   * @param {DialogOptions} options options de la modale
   * @returns promesse résolue à `true` ou `false`
   * @example
   * const confirmed = await confirm({ content: 'Êtes-vous sûr de vouloir continuer ?', confirmText: 'Oui', cancelText: 'Non' });
   * if (confirmed) { ... } else { ... }
   */
  confirm: (
    payload?: ConfirmPayload,
    options?: DialogOptions,
  ) => Promise<boolean>;
  close: <T = unknown>(value?: T) => void;
  isOpen: boolean;
};

const DialogContext = createContext<DialogContextValue | null>(null);

export function DialogProvider({
  children,
}: Readonly<{
  children: ReactNode;
}>): ReactElement {
  const idSeq = useRef<number>(1);
  const [stack, setStack] = useState<StackItem[]>([]);
  const pathname = usePathname();
  const previousPathnameRef = useRef<string>(pathname);

  const top = stack[stack.length - 1];
  const isOpen = Boolean(top);

  // Fermer toutes les modales lors du changement de page
  useEffect(() => {
    // Vérifier si le pathname a vraiment changé (éviter les re-renders inutiles)
    if (previousPathnameRef.current !== pathname && stack.length > 0) {
      // Résoudre toutes les promesses en cours avec undefined
      setStack((currentStack) => {
        currentStack.forEach((item) => {
          item.resolver(undefined);
        });
        return [];
      });
    }
    // Mettre à jour la référence du pathname précédent
    previousPathnameRef.current = pathname;
  }, [pathname, stack.length]); // Dépend du pathname et de la longueur de la pile

  const close = useCallback(<T,>(value?: T) => {
    setStack((prev) => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      last.resolver(value);
      return prev.slice(0, -1);
    });
  }, []);

  const pushItem = useCallback(
    <T,>(item: Omit<StackItem, 'id' | 'resolver' | 'rejecter'>) =>
      new Promise<T | undefined>((resolve, reject) => {
        const id = idSeq.current++;
        setStack((prev) => [
          ...prev,
          {
            ...item,
            id,
            resolver: resolve as (value: unknown) => void,
            rejecter: reject,
          },
        ]);
      }),
    [],
  );

  const open = useCallback<OpenFn>(
    (arg: unknown, options?: DialogOptions) => {
      if (typeof arg === 'function') {
        // renderer mode
        const renderFn: DialogRenderFn = arg as DialogRenderFn;
        return pushItem({ render: renderFn, options });
      }
      // payload mode (built-in chrome)
      return pushItem({
        render: null,
        options,
        basicPayload: arg as BasicModalPayload<unknown>,
      });
    },
    [pushItem],
  );

  const confirm = useCallback<DialogContextValue['confirm']>(
    async (payload, options) => {
      const result = await pushItem<boolean>({
        render: null,
        options,
        confirmPayload: payload,
      });
      return Boolean(result);
    },
    [pushItem],
  );

  const value: DialogContextValue = { open, confirm, close, isOpen };

  const Modal = useMemo(() => {
    if (!top) return null;

    if (top.render) {
      // Custom renderer
      return <>{top.render({ close })}</>;
    }

    if (top.basicPayload) {
      // Built-in Basic Modal UI
      return <BasicModalUI payload={top.basicPayload} onClose={close} />;
    }

    if (top.confirmPayload) {
      // Built-in Confirm Dialog UI
      return <ConfirmDialogUI payload={top.confirmPayload} onClose={close} />;
    }

    return null;
  }, [top, close]);

  return (
    <DialogContext.Provider value={value}>
      {children}
      <Dialog
        open={isOpen}
        onClose={() => close(undefined)}
        fullScreen={top?.options?.fullScreen}
        fullWidth={top?.options?.fullWidth}
        maxWidth={top?.options?.maxWidth}
        aria-label={top?.options?.ariaLabel}
        sx={top?.options?.sx}
      >
        {Modal}
      </Dialog>
    </DialogContext.Provider>
  );
}

function BasicModalUI<TData = unknown>({
  payload,
  onClose,
}: Readonly<{
  payload: BasicModalPayload<TData>;
  onClose: (v?: TData) => void;
}>) {
  const {
    title,
    content = null,
    cancelText = 'Annuler',
    confirmText,
    initialData,
    canConfirm,
  } = payload;

  const [data, setData] = useState<TData>(() => initialData ?? ({} as TData));

  const confirmDisabled = canConfirm ? !canConfirm(data) : false;

  const Modal = useMemo(() => {
    if (typeof content === 'function') {
      return content({
        data,
        setData,
        close: onClose,
      });
    }
    if (typeof content === 'string') {
      return <p>{content}</p>;
    }
    return content;
  }, [content, data, onClose]);

  return (
    <>
      {title && <DialogTitle>{title}</DialogTitle>}
      <DialogContent dividers>{Modal}</DialogContent>
      <DialogActions>
        <Button
          onClick={() => onClose(undefined)}
          variant="contained"
          color="secondary"
        >
          {cancelText}
        </Button>
        {confirmText && (
          <Button
            onClick={() => onClose(data)}
            variant="contained"
            color="primary"
            disabled={confirmDisabled}
          >
            {confirmText}
          </Button>
        )}
      </DialogActions>
    </>
  );
}

function ConfirmDialogUI({
  payload = {},
  onClose,
}: Readonly<{
  payload: ConfirmPayload;
  onClose: (v?: boolean) => void;
}>): ReactElement {
  const {
    title,
    content = 'Confirmer la suppression de la ligne',
    confirmText = 'Confirmer',
    cancelText = 'Annuler',
  } = payload;

  return (
    <>
      {title && <DialogTitle>{title}</DialogTitle>}
      <DialogContent dividers>
        {typeof content === 'string' ? <p>{content}</p> : content}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => onClose(false)}
          variant="contained"
          color="secondary"
        >
          {cancelText}
        </Button>
        <Button
          onClick={() => onClose(true)}
          variant="contained"
          color="primary"
        >
          {confirmText}
        </Button>
      </DialogActions>
    </>
  );
}

export function useDialog(): DialogContextValue {
  const ctx = useContext(DialogContext);
  if (!ctx) throw new Error('useDialog must be used within a <DialogProvider>');
  return ctx;
}
